import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import PageHeader from "../PageHeader";
import { trainingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import CheckinForm from "./CheckinForm";
import PlanView from "./PlanView";
import TrainingIntro from "./TrainingIntro";
import TrainingSession from "./TrainingSession";
import {
  DRAFT_MAX_AGE_MS,
  clearDraft,
  isFreshDraft,
  makeDraft,
  readDraft,
  saveDraft,
} from "./lib/draft";
import { dayKey } from "./lib/format";
import { sendLeftovers } from "./lib/useSetQueue";

// Walkthrough 1, in the spec's words.
const WHAT_IT_IS =
  "Tell it how you feel; it picks today's session from what's recovered and what's behind this week. Change anything.";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Why a plan on screen was planned again instead of started (s7): when it
 * was made, in the settings' days, and that its check-in isn't reused.
 */
function staleNote(draft, timeZone, now = Date.now()) {
  const then = dayKey(new Date(draft.plannedAt), timeZone);
  let text;
  if (then === dayKey(new Date(now), timeZone)) {
    text = `This plan was from over ${Math.round(DRAFT_MAX_AGE_MS / 3600000)} hours ago — here's a fresh one.`;
  } else if (then === dayKey(new Date(now - DAY_MS), timeZone)) {
    text = "This plan was from yesterday — here's today's.";
  } else {
    text = "This plan was from an earlier day — here's today's.";
  }
  if (draft.request?.checkin) text += " Its check-in isn't reused — change the check-in to add today's.";
  return text;
}

/**
 * What sending the leftover sets has to say (contract 2): the server's
 * notices ("Closed your previous session…"), then the sets it refused for
 * good, each with its reason. `result` is sendLeftovers' answer.
 */
function leftoverNotes(result) {
  const notices = result?.notices ?? [];
  const dropped = result?.dropped ?? [];
  const notes = notices.map((text) => ({ text }));
  if (dropped.length) {
    const sets = dropped.length === 1 ? "1 set" : `${dropped.length} sets`;
    notes.push({
      text: `${sets} from your last session couldn't be saved:`,
      items: dropped.map(({ text, reason }) => `${text} — ${reason}`),
    });
  }
  return notes;
}

/** next/'s body: only what was given (D3 — every part is optional). */
const nextBody = ({ checkin, minutes, template }) => ({
  ...(checkin ? { checkin } : {}),
  ...(minutes ? { minutes } : {}),
  ...(template ? { template } : {}),
});

/**
 * Today (walkthroughs 1–4, 10): one screen through a session's life.
 *
 *   loading ─► a session open? ─yes─► SESSION (TrainingSession) ─done─► loading
 *                 │ no
 *                 ├─ a plan draft (planned < 12 h ago)? ─► PLAN
 *                 ▼
 *              INTRO (no sessions yet,  ─► CHECK-IN ─► PLAN ─► Start ─► SESSION
 *              or settings never saved)
 *                                         (Skip)      (other options, build my own)
 *
 * The plan before Start lives in a draft (lib/draft.js), so a reload keeps
 * the person's edits (walkthrough 4); starting — or finding a session open
 * — clears it. Start is POST sessions/; a 409 means one is already open
 * (another tab, an agent): that one is loaded and shown, with a line
 * saying so. Server notices on the new session (D18's "Closed yesterday's
 * session") are the session view's to show; a plan from too long ago is
 * planned again instead of started (s7).
 *
 * Leaving a session starts the next check-in empty (f0). What just happened
 * — the session view's notice on leaving (a discard), what sending the
 * last session's queued sets turned up — is said once, until the next
 * action. Each step the person takes moves focus to its heading (a1).
 * Without the account (/users/me/ failed) nothing runs (s10).
 */
function TrainingToday() {
  const { me, meLoading, settings, exercises, applySettings } = useOutletContext() || {};
  const userId = me?.id ?? null;
  const [phase, setPhase] = useState("loading"); // loading | error | intro | checkin | plan | session
  const [loadError, setLoadError] = useState(null);
  const [session, setSession] = useState(null);
  const [resumed, setResumed] = useState(false);
  const [draft, setDraft] = useState(null);
  const [persisted, setPersisted] = useState(true);
  const [answers, setAnswers] = useState(null); // the check-in's last answers
  const [planning, setPlanning] = useState(null); // {template} while next/ runs
  const [planError, setPlanError] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  // One-time lines about what just happened (a discard, sets from the last
  // session): shown on the page Today lands on, until the next action.
  const [notes, setNotes] = useState([]);
  const mounted = useRef(true);
  const loadGen = useRef(0);
  const noteKey = useRef(0);
  // a1: a step the person took moves focus to the new step's heading once
  // it's on screen — the button they pressed is usually gone with the old
  // step, which would leave a keyboard or screen-reader user on <body>.
  const rootRef = useRef(null);
  const focusPending = useRef(false);
  const [focusTick, setFocusTick] = useState(0);
  const planGen = useRef(0);
  const planAbort = useRef(null);
  const startingRef = useRef(false);
  // Render mirror: load reads whether the settings were ever saved without
  // re-running when they change (applySettings after the intro).
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [hasHistory, setHasHistory] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      planAbort.current?.abort();
    };
  }, []);

  const moveFocus = useCallback(() => {
    focusPending.current = true;
    setFocusTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!focusPending.current || phase === "loading") return;
    const root = rootRef.current;
    // each step's heading is marked (and focusable); the session view's is
    // its first heading; a failed load has its Retry
    const marked = root?.querySelector("[data-phase-heading]");
    const target =
      marked ?? root?.querySelector(phase === "error" ? "[data-action=retry-load]" : "h1, h2");
    if (!target) return;
    focusPending.current = false; // once: later edits keep their own focus
    if (!marked && !target.matches("button") && !target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1"); // the session view's heading isn't ours to mark
    }
    target.focus();
  }, [phase, focusTick, draft]);

  const showSession = useCallback(
    (value, { wasOpen = false } = {}) => {
      // A plan still on its way can't pull the session back to the plan
      // view (s6), and a plan worked out before a session is stale after it.
      planGen.current += 1;
      planAbort.current?.abort();
      planAbort.current = null;
      setPlanning(null);
      setPlanError(null);
      clearDraft(userId);
      setDraft(null);
      setSession(value);
      setResumed(wasOpen);
      setPhase("session");
    },
    [userId],
  );

  const load = useCallback(async () => {
    const id = ++loadGen.current;
    const stale = () => !mounted.current || id !== loadGen.current;
    planGen.current += 1; // a plan still on its way belongs to the last visit
    planAbort.current?.abort();
    setPhase("loading");
    setLoadError(null);
    setPlanning(null);
    setPlanError(null);
    setStartError(null);
    try {
      const open = await trainingApi.sessions.open();
      if (stale()) return;
      if (open.data) {
        showSession(open.data);
        return;
      }
      // D14: sets still queued on this device for a session that has ended
      // (it closed itself, or finished before they got through) are sent
      // now — not only when a session view next opens. What the server says
      // about them (f9) and any it refused for good (s2/c0) is shown, on
      // whatever page Today is on by then.
      sendLeftovers(userId, null, (id, batch) => trainingApi.sets.log(id, batch))
        .then((result) => {
          const more = leftoverNotes(result);
          if (!mounted.current || !more.length) return;
          setNotes((current) => [
            ...current,
            ...more.map((note) => ({ ...note, key: `leftovers-${(noteKey.current += 1)}` })),
          ]);
        })
        .catch(() => {}); // best effort: they stay queued for next time
      const saved = readDraft(userId);
      if (saved) {
        setDraft(saved);
        setAnswers(saved.request);
        setLastRequest(saved.request);
        setPersisted(true);
        setPhase("plan");
        return;
      }
      const { data } = await trainingApi.sessions.list({ page_size: 1 });
      if (stale()) return;
      // The intro asks once (f14): with no history yet, or while the settings
      // were never saved — history that came first through an agent would
      // otherwise leave the time zone at the default's, unasked.
      const none = data?.count === 0;
      setHasHistory(!none);
      setPhase(none || settingsRef.current?.saved === false ? "intro" : "checkin");
    } catch (err) {
      if (stale()) return;
      setLoadError(apiErrorMessage(err, "Something went wrong."));
      setPhase("error");
    }
  }, [userId, showSession]);

  // /users/me/ failed: with no id there's nowhere to keep a set or a draft
  // safely (D14), so Training fails closed rather than run on memory alone.
  const noAccount = !meLoading && userId === null;

  useEffect(() => {
    // the draft and the set queue are keyed by who's signed in
    if (meLoading || userId === null) return;
    load();
  }, [meLoading, userId, load]);

  /**
   * The person acted: the last action's notes have been read — except a
   * carried one (sets a closed session didn't take), kept until the person
   * leaves the session they're logged in.
   */
  const clearNotes = () =>
    setNotes((current) => {
      const kept = current.filter((note) => note.carry);
      return kept.length === current.length ? current : kept;
    });

  /**
   * Back from a session (finished or discarded). The next check-in asks
   * about the next day, so nothing from this one is carried over (f0).
   * `notice` is the session view's own words for what happened; `carry`
   * keeps it on screen through the check-in and the next session.
   */
  const leaveSession = (notice, options) => {
    setAnswers(null);
    setLastRequest(null);
    setNotes(
      typeof notice === "string" && notice ? [{ key: "left", text: notice, carry: options?.carry === true }] : [],
    );
    moveFocus();
    load();
  };

  const keep = (value) => {
    setDraft(value);
    setPersisted(saveDraft(userId, value));
  };

  /** POST next/ — newest wins; a superseded request is aborted. */
  const requestPlan = async (request) => {
    const id = ++planGen.current;
    planAbort.current?.abort();
    const controller = new AbortController();
    planAbort.current = controller;
    setLastRequest(request);
    setPlanning({ template: request.template ?? "" });
    setPlanError(null);
    try {
      const { data } = await trainingApi.next(nextBody(request), { signal: controller.signal });
      if (!mounted.current || id !== planGen.current) return;
      keep(makeDraft({ request, next: data }));
      setStartError(null);
      setPhase("plan");
      moveFocus();
    } catch (err) {
      if (!mounted.current || id !== planGen.current) return;
      setPlanError(
        apiErrorMessage(
          err,
          request.template ? "Couldn't plan that session." : "Couldn't plan today's session.",
        ),
      );
    } finally {
      if (mounted.current && id === planGen.current) {
        setPlanning(null);
        planAbort.current = null;
      }
    }
  };

  /**
   * The plan on screen is from too long ago (s7): plan today's instead of
   * starting it. Its check-in was another day's answers, so it isn't sent
   * again — the same rule as f0 — and, as the new draft's request has none,
   * Change the check-in starts empty.
   */
  const replanStale = () => {
    // (the stored draft needs no clearing: readDraft drops it as stale, and
    // the new plan replaces it)
    const note = staleNote(draft, settings?.timezone);
    setNotes((current) => [...current.filter((kept) => kept.carry), { key: "stale", text: note }]);
    requestPlan({ checkin: null, minutes: null, template: null });
  };

  // A tab left on the plan overnight: re-plan when it comes back into view.
  // The handler reads this render's state through a ref, so the listener is
  // added once.
  const onVisible = useRef(null);
  onVisible.current = () => {
    if (document.visibilityState !== "visible") return;
    if (phase !== "plan" || !draft || planning !== null || startingRef.current) return;
    if (!isFreshDraft(draft)) replanStale();
  };
  useEffect(() => {
    const listener = () => onVisible.current?.();
    document.addEventListener("visibilitychange", listener);
    return () => document.removeEventListener("visibilitychange", listener);
  }, []);

  const start = async () => {
    if (startingRef.current || !draft) return;
    clearNotes();
    if (!isFreshDraft(draft)) {
      replanStale();
      return;
    }
    startingRef.current = true;
    setStarting(true);
    setStartError(null);
    const { plan, request } = draft;
    const minutes = plan.minutes ?? request.minutes;
    let conflict = false;
    try {
      const response = await trainingApi.sessions.start({
        plan,
        template: plan.template,
        ...(request.checkin ? { checkin: request.checkin } : {}),
        ...(minutes ? { minutes } : {}),
      });
      if (!mounted.current) return;
      if (response.conflict) {
        conflict = true;
        const { data } = await trainingApi.sessions.get(response.conflict.session);
        if (!mounted.current) return;
        showSession(data, { wasOpen: true });
        moveFocus();
        return;
      }
      showSession(response.data);
      moveFocus();
    } catch (err) {
      if (!mounted.current) return;
      setStartError(
        conflict
          ? `A session is already open (another tab, or an agent), but it didn't load: ${apiErrorMessage(err, "try again.")}`
          : apiErrorMessage(err, "Couldn't start the session."),
      );
    } finally {
      startingRef.current = false;
      if (mounted.current) setStarting(false);
    }
  };

  const notesAlert =
    notes.length > 0 ? (
      <Alert role="status">
        <AlertDescription className="flex flex-col gap-2 text-foreground">
          {/* the spaces keep the lines apart in the text an agent or a
              screen reader gets; in these flex columns they cost nothing */}
          {notes.map((note, index) => (
            <div key={note.key} className="flex flex-col">
              {index > 0 && " "}
              <p className="m-0">{note.text}</p>
              {note.items?.length > 0 && (
                <>
                  {" "}
                  <ul className="m-0 mt-1 flex list-disc flex-col gap-1 pl-4">
                    {note.items.map((item, at) => (
                      <li key={at}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ))}
        </AlertDescription>
      </Alert>
    ) : null;

  if (noAccount) {
    return (
      <div className="page-container mt-6 pt-4 pb-8">
        <div className="mx-auto max-w-2xl">
          <PageHeader title="Today" subtitle={WHAT_IT_IS} />
          <Alert variant="destructive">
            <AlertDescription className="flex flex-wrap items-center gap-3">
              <span className="flex-1">
                Couldn&apos;t load your account, so sets can&apos;t be kept safely on this device.
                Reload to try again.
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-11 md:h-8"
                onClick={() => window.location.reload()}
              >
                Reload
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (phase === "session" && session) {
    return (
      <div ref={rootRef} className="page-container mt-6 pt-4 pb-8">
        <div className="mx-auto flex max-w-2xl flex-col gap-4">
          {notesAlert}
          {resumed && (
            <Alert role="status">
              <AlertDescription className="text-foreground">
                A session was already open — from another tab, or an agent — so here it is. Finish
                or discard it to start a new one.
              </AlertDescription>
            </Alert>
          )}
          <TrainingSession session={session} onFinished={leaveSession} onSessionChange={setSession} />
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="page-container mt-6 pt-4 pb-8">
      <div className="mx-auto max-w-2xl">
        <PageHeader title="Today" subtitle={WHAT_IT_IS} />
        {notesAlert && <div className="mb-4">{notesAlert}</div>}
        {phase === "loading" && (
          <div className="py-12 text-center" role="status">
            <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="sr-only">Loading today…</span>
          </div>
        )}
        {phase === "error" && (
          <Alert variant="destructive">
            <AlertDescription className="flex flex-wrap items-center gap-3">
              <span className="flex-1">Couldn&apos;t load today: {loadError}</span>
              <Button
                size="sm"
                variant="outline"
                className="h-11 md:h-8"
                data-action="retry-load"
                onClick={() => {
                  clearNotes();
                  load();
                }}
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {phase === "intro" && (
          <TrainingIntro
            settings={settings}
            firstSession={!hasHistory}
            onContinue={(saved) => {
              clearNotes();
              if (saved !== settings) applySettings?.(saved);
              setPhase("checkin");
              moveFocus();
            }}
          />
        )}
        {phase === "checkin" && (
          <CheckinForm
            settings={settings}
            initial={answers}
            busy={planning !== null}
            error={planError}
            onSubmit={({ checkin, minutes }) => {
              clearNotes();
              setAnswers({ checkin, minutes });
              requestPlan({ checkin, minutes, template: null });
            }}
          />
        )}
        {phase === "plan" && draft && (
          <PlanView
            next={draft.next}
            plan={draft.plan}
            edited={draft.edited}
            settings={settings}
            exercises={exercises ?? []}
            // a plan from too long ago (its re-plan failed): its check-in was
            // another day's (f0), for an exercise brought in too
            checkin={isFreshDraft(draft) ? (draft.request?.checkin ?? null) : null}
            persisted={persisted}
            planning={planning ? planning.template : null}
            planError={planError}
            onRetry={
              lastRequest
                ? () => {
                    clearNotes();
                    requestPlan(lastRequest);
                  }
                : undefined
            }
            starting={starting}
            startError={startError}
            onItemsChange={(items, how) => {
              // the engine's numbers landing for an exercise brought in (as a
              // reload asks again, on arrival) isn't the person acting
              if (!how?.answered) clearNotes();
              keep({ ...draft, plan: { ...draft.plan, items }, edited: true });
            }}
            onChooseTemplate={(template) => {
              clearNotes();
              // A plan from too long ago (its re-plan failed, so it's still on
              // screen): its check-in and minutes were another day's, so they
              // aren't sent again — replanStale's rule (f0).
              requestPlan(
                isFreshDraft(draft) ? { ...draft.request, template } : { checkin: null, minutes: null, template },
              );
            }}
            onBuildOwn={() => {
              clearNotes();
              moveFocus();
              keep({
                ...draft,
                plan: {
                  template: "custom",
                  units: draft.plan.units ?? settings?.units ?? "lb",
                  minutes: draft.plan.minutes ?? draft.request.minutes ?? settings?.session_minutes,
                  reason: "Your own session.",
                  items: [],
                },
                edited: true,
              });
            }}
            onUseSuggested={() => {
              clearNotes();
              moveFocus();
              keep({ ...draft, plan: draft.next.plan, edited: false });
            }}
            onChangeCheckin={() => {
              clearNotes();
              setPlanError(null);
              // A plan from too long ago (its re-plan failed, so it's still
              // on screen) asked about another day: start empty (f0).
              setAnswers(
                isFreshDraft(draft) ? { checkin: draft.request.checkin, minutes: draft.request.minutes } : null,
              );
              setPhase("checkin");
              moveFocus();
            }}
            onStart={start}
          />
        )}
      </div>
    </div>
  );
}

export default TrainingToday;
